import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDarkMode } from "../App";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import styles from "./Header.module.css";
import publicStyles from "./PublicHeader.module.css";

type Props = {
  onSignInClick: () => void;
  onSignUpClick: () => void;
};

export default function PublicHeader({ onSignInClick, onSignUpClick }: Props) {
  const { darkMode, setDarkMode } = useDarkMode();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const handleMobileSignIn = () => {
    setMobileOpen(false);
    onSignInClick();
  };

  const handleMobileSignUp = () => {
    setMobileOpen(false);
    onSignUpClick();
  };

  const headerClass = [
    styles.header,
    styles.headerPublic,
    styles.headerSolid,
    darkMode ? styles.dark : "",
  ]
    .filter(Boolean)
    .join(" ");

  const browseActive =
    location.pathname === "/" ||
    location.pathname.startsWith("/search") ||
    location.pathname.startsWith("/product");
  const aboutActive = location.pathname.startsWith("/about");

  return (
    <header className={headerClass}>
      <div className={styles.headerAccent} aria-hidden />
      <div className={styles.headerContent}>
        <Link to="/" className={styles.logo}>
          <span className={styles.logoMark} aria-hidden>
            <DirectionsCarIcon fontSize="small" />
          </span>
          <span className={styles.logoText}>Drive Now!</span>
        </Link>

        <nav className={styles.desktopNav} aria-label="Public navigation">
          <Link
            to="/"
            className={`${styles.navLink} ${browseActive ? styles.navLinkActive : ""}`}
            data-testid="public-nav-browse"
          >
            Browse
          </Link>
          <Link
            to="/about"
            className={`${styles.navLink} ${aboutActive ? styles.navLinkActive : ""}`}
            data-testid="public-nav-about"
          >
            About
          </Link>
        </nav>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => setDarkMode((prev) => !prev)}
            aria-label="Toggle dark mode"
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </button>

          <div className={publicStyles.authButtons}>
            <button
              type="button"
              className={publicStyles.signInButton}
              onClick={onSignInClick}
            >
              Sign In
            </button>
            <button
              type="button"
              className={publicStyles.signUpButton}
              onClick={onSignUpClick}
            >
              Sign Up
            </button>
          </div>

          <button
            type="button"
            className={styles.mobileMenuButton}
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      <div className={`${styles.mobileNav} ${mobileOpen ? styles.mobileNavOpen : ""}`}>
        <div className={styles.mobileNavContent}>
          <Link
            to="/"
            className={styles.mobileNavLink}
            onClick={() => setMobileOpen(false)}
            data-testid="public-nav-browse-mobile"
          >
            <span>Browse cars</span>
          </Link>
          <Link
            to="/about"
            className={styles.mobileNavLink}
            onClick={() => setMobileOpen(false)}
            data-testid="public-nav-about-mobile"
          >
            <span>About Us</span>
          </Link>

          <div className={styles.mobileNavDivider} />

          <button
            type="button"
            className={styles.mobileNavLink}
            onClick={handleMobileSignIn}
          >
            <span>Sign In</span>
          </button>
          <button
            type="button"
            className={`${styles.mobileNavLink} ${styles.mobileSellCta}`}
            onClick={handleMobileSignUp}
          >
            <span>Sign Up</span>
          </button>

          <div className={styles.mobileNavDivider} />

          <button
            type="button"
            className={styles.mobileNavLink}
            onClick={() => setDarkMode((prev) => !prev)}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            <span>{darkMode ? "Light Mode" : "Dark Mode"}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
